/**
 * @fileoverview Academy Configuration — Administration
 * Manage Fee Plans, Coach Roles, License Levels, Age Groups in one place.
 * Data persisted to localStorage (key: "clubos_academy_config").
 * Replace localStorage calls with API calls once the backend endpoint is ready.
 */

import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";

import PropTypes from "prop-types";
import { academyService } from "api";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// ── Default config ─────────────────────────────────────────────────────────────
const DEFAULTS = {
  feePlans: [
    { value: "Standard",         label: "Standard",          discountPct: 0 },
    { value: "Scholarship (25%)", label: "Scholarship (25%)", discountPct: 25 },
    { value: "Full Waiver",       label: "Full Waiver",       discountPct: 100 },
  ],
  coachRoles: [
    "Head Coach", "Assistant Coach", "GK Coach", "Fitness Coach", "Grassroots Coach", "Physio",
  ],
  licenseLevels: ["A", "B", "C", "D", "Grassroots"],
  ageGroups: ["U10", "U13", "U15", "U18"],
  trainingDays: ["Sat-Sun", "Fri-Sat-Sun"],
  timeSlots: ["06:00-08:00", "07:00-09:00", "16:00-18:00", "17:00-19:00", "18:00-20:00"],
};

const STORAGE_KEY = "clubos_academy_config";

function loadConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS };
  } catch { return { ...DEFAULTS }; }
}

function saveConfig(cfg) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
}

// ── Generic editable list section ─────────────────────────────────────────────
function ListSection({ title, icon, items, onAdd, onDelete, description }) {
  const [input, setInput] = useState("");
  const [err, setErr]     = useState("");

  const handleAdd = () => {
    const val = input.trim();
    if (!val) { setErr("Value cannot be empty."); return; }
    if (items.some((i) => (typeof i === "string" ? i : i.value) === val)) {
      setErr("Already exists."); return;
    }
    setErr("");
    setInput("");
    onAdd(val);
  };

  return (
    <Card sx={{ height: "100%" }}>
      <SoftBox p={2.5}>
        <SoftBox display="flex" alignItems="center" gap={1} mb={0.5}>
          <Icon sx={{ color: "info.main", fontSize: "1.1rem" }}>{icon}</Icon>
          <SoftTypography variant="h6" fontWeight="bold" color="dark">{title}</SoftTypography>
        </SoftBox>
        {description && (
          <SoftTypography variant="caption" color="text" display="block" mb={1.5}>{description}</SoftTypography>
        )}

        {/* Items */}
        <SoftBox display="flex" flexWrap="wrap" gap={1} mb={2} minHeight={32}>
          {items.map((item) => {
            const label = typeof item === "string" ? item : item.label;
            const key   = typeof item === "string" ? item : item.value;
            return (
              <Chip
                key={key}
                label={label}
                onDelete={() => onDelete(key)}
                size="small"
                sx={{
                  fontWeight: 500,
                  backgroundColor: "rgba(26,115,232,0.08)",
                  color: "#344767",
                  "& .MuiChip-deleteIcon": { color: "#7b809a", fontSize: "0.9rem" },
                }}
              />
            );
          })}
          {items.length === 0 && (
            <SoftTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>
              No items yet
            </SoftTypography>
          )}
        </SoftBox>

        {/* Add input */}
        <SoftBox display="flex" gap={1} alignItems="flex-start">
          <TextField size="small" fullWidth placeholder={`Add new ${title.toLowerCase().replace(/s$/, "")}…`}
            value={input} onChange={(e) => { setInput(e.target.value); setErr(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            error={Boolean(err)} helperText={err} />
          <SoftButton variant="gradient" color="info" size="small" onClick={handleAdd}
            sx={{ minWidth: 0, px: 2, flexShrink: 0, mt: err ? 0 : 0 }}>
            <Icon>add</Icon>
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

ListSection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  description: PropTypes.string,
};

// ── Fee plan section (special — has discount %) ────────────────────────────────
function FeePlanSection({ plans, onAdd, onDelete }) {
  const [label, setLabel] = useState("");
  const [pct, setPct]     = useState("");
  const [err, setErr]     = useState("");

  const handleAdd = () => {
    const l = label.trim();
    if (!l) { setErr("Plan name is required."); return; }
    const d = Number(pct);
    if (isNaN(d) || d < 0 || d > 100) { setErr("Discount must be 0–100."); return; }
    if (plans.some((p) => p.value === l)) { setErr("Plan already exists."); return; }
    setErr(""); setLabel(""); setPct("");
    onAdd({ value: l, label: l, discountPct: d });
  };

  const BADGE_COLORS = ["info", "success", "warning", "error", "secondary"];

  return (
    <Card>
      <SoftBox p={2.5}>
        <SoftBox display="flex" alignItems="center" gap={1} mb={0.5}>
          <Icon sx={{ color: "info.main", fontSize: "1.1rem" }}>payments</Icon>
          <SoftTypography variant="h6" fontWeight="bold" color="dark">Fee Plans</SoftTypography>
        </SoftBox>
        <SoftTypography variant="caption" color="text" display="block" mb={1.5}>
          Fee plans available when adding or editing a player
        </SoftTypography>

        {/* Table */}
        <SoftBox sx={{ border: "1px solid #f0f0f0", borderRadius: 2, overflow: "hidden", mb: 2 }}>
          {/* Header */}
          <SoftBox display="flex" px={2} py={1} sx={{ backgroundColor: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>PLAN NAME</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 100px", textAlign: "center" }}>DISCOUNT</SoftTypography>
            <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>BADGE</SoftTypography>
            <SoftBox sx={{ width: 36 }} />
          </SoftBox>
          {plans.map((plan, idx) => (
            <SoftBox key={plan.value} display="flex" alignItems="center" px={2} py={1.25}
              sx={{ borderBottom: idx < plans.length - 1 ? "1px solid #f5f5f5" : "none",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.01)" } }}>
              <SoftTypography variant="button" fontWeight="medium" color="dark" sx={{ flex: 1 }}>
                {plan.label}
              </SoftTypography>
              <SoftTypography variant="caption" color="text" sx={{ flex: "0 0 100px", textAlign: "center" }}>
                {plan.discountPct === 0 ? "—" : `${plan.discountPct}% off`}
              </SoftTypography>
              <SoftBox sx={{ flex: "0 0 80px", textAlign: "center" }}>
                <SoftBadge badgeContent={plan.label} color={BADGE_COLORS[idx % BADGE_COLORS.length]} size="xs" container />
              </SoftBox>
              <IconButton size="small" onClick={() => onDelete(plan.value)} sx={{ width: 28, height: 28 }}>
                <Icon sx={{ fontSize: "0.9rem", color: "error.light" }}>delete</Icon>
              </IconButton>
            </SoftBox>
          ))}
          {plans.length === 0 && (
            <SoftBox p={2} textAlign="center">
              <SoftTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>No plans</SoftTypography>
            </SoftBox>
          )}
        </SoftBox>

        {/* Add row */}
        {err && <Alert severity="error" sx={{ mb: 1 }}>{err}</Alert>}
        <SoftBox display="flex" gap={1}>
          <TextField size="small" label="Plan Name" value={label}
            onChange={(e) => { setLabel(e.target.value); setErr(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            sx={{ flex: 1 }} />
          <TextField size="small" label="Discount %" type="number" value={pct}
            onChange={(e) => { setPct(e.target.value); setErr(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            sx={{ width: 110 }} inputProps={{ min: 0, max: 100 }} />
          <SoftButton variant="gradient" color="info" size="small" onClick={handleAdd}
            sx={{ minWidth: 0, px: 2 }}>
            <Icon>add</Icon>
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

FeePlanSection.propTypes = {
  plans: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// ── Main ──────────────────────────────────────────────────────────────────────
function AcademyConfig() {
  const [config, setConfig] = useState(loadConfig);
  const [saved, setSaved]   = useState(false);

  // Load age groups + training days from DB on mount
  useEffect(() => {
    Promise.all([
      academyService.getAgeGroups().catch(() => null),
      academyService.getTrainingDaySchedules().catch(() => null),
      academyService.getCoachRoles().catch(() => null),
      academyService.getTimeSlots().catch(() => null),
    ]).then(([ag, td, roles, ts]) => {
      setConfig((c) => ({
        ...c,
        ...(Array.isArray(ag)    && ag.length    ? { ageGroups:    ag }    : {}),
        ...(Array.isArray(td)    && td.length    ? { trainingDays: td }    : {}),
        ...(Array.isArray(roles) && roles.length ? { coachRoles:   roles } : {}),
        ...(Array.isArray(ts)    && ts.length    ? { timeSlots:    ts }    : {}),
      }));
    });
  }, []);

  // Persist non-ageGroup config keys to localStorage so other pages still work
  useEffect(() => { saveConfig(config); }, [config]);

  const update = (key, val) => { setConfig((c) => ({ ...c, [key]: val })); setSaved(false); };

  // Fee plans
  const addPlan    = (plan)  => update("feePlans", [...config.feePlans, plan]);
  const delPlan    = (value) => update("feePlans", config.feePlans.filter((p) => p.value !== value));

  // Generic list helpers
  const addItem  = (key) => (val) => update(key, [...config[key], val]);
  const delItem  = (key) => (val) => update(key, config[key].filter((x) => x !== val));

  const handleReset = () => { setConfig({ ...DEFAULTS }); };

  const handleSave = async () => {
    saveConfig(config);
    await Promise.all([
      academyService.saveAgeGroups(config.ageGroups).catch(() => {}),
      academyService.saveTrainingDaySchedules(config.trainingDays).catch(() => {}),
      academyService.saveCoachRoles(config.coachRoles).catch(() => {}),
      academyService.saveTimeSlots(config.timeSlots).catch(() => {}),
    ]);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Academy Configuration</SoftTypography>
            <SoftTypography variant="body2" color="text">
              Manage fee plans, coach roles, license levels and age groups
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1} alignItems="center">
            {saved && (
              <SoftBox display="flex" alignItems="center" gap={0.5}>
                <Icon sx={{ color: "success.main", fontSize: "1rem" }}>check_circle</Icon>
                <SoftTypography variant="caption" color="success">Saved</SoftTypography>
              </SoftBox>
            )}
            <SoftButton variant="outlined" color="secondary" size="small" onClick={handleReset}>
              Reset to Defaults
            </SoftButton>
            <SoftButton variant="gradient" color="info" startIcon={<Icon>save</Icon>} onClick={handleSave}>
              Save Changes
            </SoftButton>
          </SoftBox>
        </SoftBox>

        <Alert severity="info" sx={{ mb: 3 }}>
          Changes made here are reflected immediately across all Academy forms (Fee Plan dropdowns, Role dropdowns, etc.).
        </Alert>

        {/* Fee Plans — full width */}
        <SoftBox mb={3}>
          <FeePlanSection plans={config.feePlans} onAdd={addPlan} onDelete={delPlan} />
        </SoftBox>

        {/* Roles + Licenses */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={4}>
            <ListSection
              title="Coach Roles"
              icon="manage_accounts"
              description="Roles available in the Coach form (saved to database)"
              items={config.coachRoles}
              onAdd={addItem("coachRoles")}
              onDelete={delItem("coachRoles")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ListSection
              title="License Levels"
              icon="verified"
              description="License options in the Coach form"
              items={config.licenseLevels}
              onAdd={addItem("licenseLevels")}
              onDelete={delItem("licenseLevels")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ListSection
              title="Age Groups"
              icon="people"
              description="Age groups used for Batches and Players"
              items={config.ageGroups}
              onAdd={addItem("ageGroups")}
              onDelete={delItem("ageGroups")}
            />
          </Grid>
        </Grid>

        {/* Training Days + Time Slots */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <ListSection
              title="Training Day Schedules"
              icon="event"
              description="Schedule options shown in the Batch creation dropdown (e.g. Sat-Sun)"
              items={config.trainingDays}
              onAdd={addItem("trainingDays")}
              onDelete={delItem("trainingDays")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListSection
              title="Time Slots"
              icon="schedule"
              description="Time slot options shown in the Batch creation dropdown (e.g. 06:00-08:00)"
              items={config.timeSlots}
              onAdd={addItem("timeSlots")}
              onDelete={delItem("timeSlots")}
            />
          </Grid>
        </Grid>

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />
    </DashboardLayout>
  );
}

export default AcademyConfig;
